import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ExternalLink, CheckCircle2, Circle, Clock, AlertCircle, Pencil, Check, X, Trash2 } from 'lucide-react';
import { T, springs } from '../../lib/tokens';
import { Glass } from '../glass/Glass';
import { supabase } from '../../lib/supabase';

const STATUS_ICON = {
  pending: { icon: Clock, color: T.textMuted },
  processing: { icon: Clock, color: T.amber },
  complete: { icon: CheckCircle2, color: T.green },
  error: { icon: AlertCircle, color: T.red },
};

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
  border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.8)',
  fontFamily: 'inherit', color: T.text, outline: 'none',
};

export const MeetingCard = ({ meeting, actions, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [draftSummary, setDraftSummary] = useState(meeting.summary || '');
  const [draftDate, setDraftDate] = useState(meeting.meeting_date);
  const summaryRef = useRef(null);
  const { icon: StatusIcon, color: statusColor } = STATUS_ICON[meeting.status] || STATUS_ICON.pending;

  const dateStr = new Date(meeting.meeting_date).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });

  useEffect(() => { if (editingSummary && summaryRef.current) summaryRef.current.focus(); }, [editingSummary]);

  const toggleAction = async (action) => {
    const done = !action.is_done;
    await supabase.from('meeting_actions').update({
      is_done: done,
      done_at: done ? new Date().toISOString() : null,
    }).eq('id', action.id);
    if (action.task_id) {
      await supabase.from('tasks').update({
        done,
        done_at: done ? new Date().toISOString() : null,
      }).eq('id', action.task_id);
    }
    onUpdate?.();
  };

  const saveSummary = async () => {
    setEditingSummary(false);
    if (draftSummary.trim() !== (meeting.summary || '').trim()) {
      await supabase.from('meetings').update({ summary: draftSummary.trim() }).eq('id', meeting.id);
      onUpdate?.();
    }
  };

  const saveDate = async () => {
    setEditingDate(false);
    if (draftDate !== meeting.meeting_date) {
      await supabase.from('meetings').update({ meeting_date: draftDate }).eq('id', meeting.id);
      onUpdate?.();
    }
  };

  const deleteAction = async (action) => {
    if (action.task_id) {
      await supabase.from('tasks').delete().eq('id', action.task_id);
    }
    await supabase.from('meeting_actions').delete().eq('id', action.id);
    onUpdate?.();
  };

  const summarySnippet = meeting.summary
    ? meeting.summary.length > 120 ? meeting.summary.slice(0, 120) + '...' : meeting.summary
    : meeting.status === 'pending' ? 'Awaiting processing...' : 'No summary';

  return (
    <Glass style={{ padding: open ? 20 : '14px 20px' }} hover={false} specular={false}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
      >
        <StatusIcon size={14} color={statusColor} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{dateStr}</span>
            {actions.length > 0 && (
              <span style={{
                fontSize: 10, color: T.textMuted, background: 'rgba(0,0,0,0.04)',
                padding: '1px 6px', borderRadius: 6,
              }}>
                {actions.filter(a => !a.is_done).length}/{actions.length} actions
              </span>
            )}
          </div>
          {!open && (
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {summarySnippet}
            </div>
          )}
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={springs.snappy}>
          <ChevronDown size={14} color={T.textMuted} />
        </motion.div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ marginTop: 16 }}>
              {/* Editable date */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                {editingDate ? (
                  <>
                    <input
                      type="date"
                      value={draftDate}
                      onChange={e => setDraftDate(e.target.value)}
                      style={{ ...inputStyle, width: 'auto', padding: '4px 10px', fontSize: 12 }}
                    />
                    <button onClick={saveDate} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                      <Check size={14} color={T.green} />
                    </button>
                    <button onClick={() => { setDraftDate(meeting.meeting_date); setEditingDate(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                      <X size={14} color={T.textMuted} />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={e => { e.stopPropagation(); setEditingDate(true); }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: 11, color: T.textMuted,
                    }}
                  >
                    <Pencil size={10} /> Edit date
                  </button>
                )}
              </div>

              {/* Summary */}
              <div style={{ marginBottom: 16 }}>
                {editingSummary ? (
                  <div>
                    <textarea
                      ref={summaryRef}
                      value={draftSummary}
                      onChange={e => setDraftSummary(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Escape') { setDraftSummary(meeting.summary || ''); setEditingSummary(false); } }}
                      style={{ ...inputStyle, minHeight: 120, resize: 'vertical', lineHeight: 1.6 }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={saveSummary} style={{
                        padding: '4px 12px', borderRadius: 6, border: 'none',
                        background: T.blue, color: '#fff', cursor: 'pointer',
                        fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                      }}>Save</button>
                      <button onClick={() => { setDraftSummary(meeting.summary || ''); setEditingSummary(false); }} style={{
                        padding: '4px 12px', borderRadius: 6, border: 'none',
                        background: 'rgba(0,0,0,0.04)', color: T.textMid, cursor: 'pointer',
                        fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                      }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={e => { e.stopPropagation(); setDraftSummary(meeting.summary || ''); setEditingSummary(true); }}
                    style={{
                      fontSize: 13, lineHeight: 1.6, color: T.textMid,
                      padding: 16, borderRadius: 12,
                      background: 'rgba(0,0,0,0.02)',
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                    title="Click to edit summary"
                  >
                    {meeting.summary || <span style={{ color: T.textLight, fontStyle: 'italic' }}>No summary yet. Click to add.</span>}
                    <Pencil size={10} color={T.textLight} style={{ position: 'absolute', top: 8, right: 8 }} />
                  </div>
                )}
              </div>

              {/* Transcript link */}
              {meeting.transcript_url && (
                <a
                  href={meeting.transcript_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 11, color: T.blue, textDecoration: 'none',
                    marginBottom: 16,
                  }}
                >
                  <ExternalLink size={11} /> View transcript
                </a>
              )}

              {/* Actions */}
              {actions.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                    Actions
                  </div>
                  {actions.map(action => (
                    <div
                      key={action.id}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        padding: '6px 0',
                      }}
                    >
                      <div
                        onClick={(e) => { e.stopPropagation(); toggleAction(action); }}
                        style={{ cursor: 'pointer', flexShrink: 0, marginTop: 1 }}
                      >
                        {action.is_done
                          ? <CheckCircle2 size={14} color={T.green} />
                          : <Circle size={14} color={T.textLight} />
                        }
                      </div>
                      <div style={{ flex: 1 }}>
                        <span style={{
                          fontSize: 12, color: action.is_done ? T.textMuted : T.text,
                          textDecoration: action.is_done ? 'line-through' : 'none',
                        }}>
                          {action.description}
                        </span>
                        <span style={{
                          fontSize: 10, marginLeft: 6,
                          color: action.assignee === 'mike' ? T.blue : T.textLight,
                          fontWeight: 500,
                        }}>
                          {action.assignee === 'mike' ? 'You' : 'Founder'}
                        </span>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); deleteAction(action); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.3, flexShrink: 0 }}
                        title="Delete action"
                      >
                        <Trash2 size={11} color={T.textMuted} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Glass>
  );
};
